// src/pages/ActivitiesBuilder/components/ActivityGenerate.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Chip,
  IconButton,
  Paper,
  Divider,
  Rating,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  AutoAwesome as GenerateIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  LightbulbOutlined as TipIcon
} from '@mui/icons-material';

import { Trip } from '../../../contexts/ItineraryContext';
import { useActivity, Activity } from '../../../contexts/ActivityContext';
import ActivityDetailDialog from './ActivityDetailDialog';

interface ActivityGenerateProps {
  trip: Trip;
  onSuccess: (message: string) => void;
}

const promptTemplates = [
  {
    label: 'Family Activities',
    value: 'family-friendly activities for kids',
    description: 'Activities suitable for families with children'
  },
  {
    label: 'Outdoor Adventures',
    value: 'outdoor adventure activities',
    description: 'Hiking, biking, kayaking, and other outdoor adventures'
  },
  {
    label: 'Cultural Experiences',
    value: 'cultural experiences and historic sites',
    description: 'Museums, historic sites, and cultural experiences'
  },
  {
    label: 'Foodie Experiences',
    value: 'unique food experiences and culinary tours',
    description: 'Food tours, cooking classes, and unique restaurants'
  },
  {
    label: 'Luxury Experiences',
    value: 'luxury and high-end experiences',
    description: 'Premium experiences and exclusive activities'
  },
  {
    label: 'Budget-Friendly Options',
    value: 'budget-friendly activities under $30',
    description: 'Affordable activities that won\'t break the bank'
  },
  {
    label: 'Hidden Gems',
    value: 'hidden gems and off-the-beaten-path experiences',
    description: 'Lesser-known activities away from tourist crowds'
  }
];

const ActivityGenerate: React.FC<ActivityGenerateProps> = ({ trip, onSuccess }) => {
  // State
  const [prompt, setPrompt] = useState('');
  const [generatedActivities, setGeneratedActivities] = useState<Activity[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedPromptTemplate, setSelectedPromptTemplate] = useState('');
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Get activities context
  const { 
    generateActivities, 
    saveActivity, 
    unsaveActivity
  } = useActivity();

  // Handle prompt template selection
  const handleSelectPromptTemplate = (template: string) => {
    const selectedTemplate = promptTemplates.find(t => t.value === template);
    if (selectedTemplate) {
      setSelectedPromptTemplate(template);
      setPrompt(`Recommend ${selectedTemplate.value} in ${trip.destination} for a ${trip.purpose || 'vacation'} trip.`);
    }
  };

  // Handle generate
  const handleGenerate = async () => {
     if (!prompt.trim()) return;

     // Debug the trip data

     // Check specifically for missing destination
     if (!trip || !trip.destination) {
        // Create a location fallback using the trip title
        const fallbackDestination = trip?.title?.split('-')[0]?.trim() || 'Pittsburgh';

        // Clone the trip object and add the fallback destination
        const updatedTrip = {
           ...trip,
           destination: fallbackDestination,
        };

        // Use the updated trip with the fallback destination
        setIsGenerating(true);
        try {
           const results = await generateActivities(prompt, trip.id, updatedTrip);
           setGeneratedActivities(results);
        } catch (error) {
           console.error('Failed to generate activities:', error);
        } finally {
           setIsGenerating(false);
        }
     } else {
        // Normal flow when destination exists
        setIsGenerating(true);
        try {
           const results = await generateActivities(prompt, trip.id, trip);
           setGeneratedActivities(results);
        } catch (error) {
           console.error('Failed to generate activities:', error);
        } finally {
           setIsGenerating(false);
        }
     }
  };

  // Handle save/unsave
  const handleToggleSave = async (activity: Activity) => {
    if (activity.isSaved) {
      const success = await unsaveActivity(activity.id);
      if (success) {
        setGeneratedActivities(prev => 
          prev.map(a => a.id === activity.id ? { ...a, isSaved: false } : a)
        );
        onSuccess('Activity removed from saved activities');
      }
    } else {
      const activityWithTrip = {
        ...activity,
        tripId: trip.id
      };
      const success = await saveActivity(activityWithTrip);
      if (success) {
        setGeneratedActivities(prev => 
          prev.map(a => a.id === activity.id ? { ...a, isSaved: true } : a)
        );
        onSuccess('Activity saved successfully');
      }
    }
  };

  // Open activity details
  const handleOpenDetails = (activity: Activity) => {
    setSelectedActivity(activity);
    setDetailDialogOpen(true);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Generate Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Generate Activity Ideas with AI
        </Typography>
        
        <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TipIcon color="info" sx={{ mr: 1 }} />
            <Typography variant="subtitle2">
              AI Prompt Tips
            </Typography>
          </Box>
          <Typography variant="body2">
            For best results, include details about your interests, preferences, and any specific requirements for the activities.
            Try to be as specific as possible about what you're looking for.
          </Typography>
        </Paper>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Choose a Template</InputLabel>
          <Select
            value={selectedPromptTemplate}
            onChange={(e) => handleSelectPromptTemplate(e.target.value as string)}
            label="Choose a Template"
          >
            <MenuItem value="">
              <em>Select a template (optional)</em>
            </MenuItem>
            {promptTemplates.map((template) => (
              <MenuItem key={template.value} value={template.value}>
                {template.label} - {template.description}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder={`Ask AI to generate activities for your trip to ${trip.destination}...`}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          sx={{ mb: 2 }}
        />
        
        <Button
          variant="contained"
          startIcon={isGenerating ? <CircularProgress size={20} /> : <GenerateIcon />}
          onClick={handleGenerate}
          disabled={isGenerating || !prompt.trim()}
          sx={{ width: '100%', py: 1 }}
        >
          {isGenerating ? 'Generating...' : 'Generate Activity Ideas'}
        </Button>
      </Box>
      
      {/* Generated Results */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {generatedActivities.length > 0 ? (
          <>
            <Typography variant="subtitle1" gutterBottom>
              AI-Generated Activities for {trip.destination}
            </Typography>
            
            <Grid container spacing={2}>
              {generatedActivities.map((activity) => (
                <Grid item xs={12} sm={6} key={activity.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {activity.imageUrl && (
                      <CardMedia
                        component="img"
                        height="160"
                        image={activity.imageUrl}
                        alt={activity.title}
                      />
                    )}
                    
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="h6" component="div">
                          {activity.title}
                        </Typography>
                        <IconButton
                          color={activity.isSaved ? 'primary' : 'default'}
                          onClick={() => handleToggleSave(activity)}
                        >
                          {activity.isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                        </IconButton>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {activity.description}
                      </Typography>
                      
                      <Divider sx={{ my: 1 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2">
                          {activity.location}
                        </Typography>
                        
                        {activity.cost !== undefined && (
                          <Typography variant="subtitle2" fontWeight="bold">
                            ${activity.cost.toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                        <Chip 
                          label={activity.type.charAt(0).toUpperCase() + activity.type.slice(1)} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                        />
                        
                        {activity.duration && (
                          <Chip label={activity.duration} size="small" variant="outlined" />
                        )}
                        
                        {activity.tags && activity.tags.map((tag) => (
                          <Chip key={tag} label={tag} size="small" />
                        ))}
                      </Box>
                      
                      <Chip 
                        label="AI Generated" 
                        size="small" 
                        color="secondary" 
                        icon={<GenerateIcon />} 
                        sx={{ mt: 1 }}
                      />
                    </CardContent>
                    
                    <CardActions>
                      <Button size="small" onClick={() => handleOpenDetails(activity)}>
                        View Details
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            p: 3 
          }}>
            <GenerateIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6">
              Let AI inspire your trip to {trip.destination}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 500 }}>
              Enter a prompt to generate personalized activity ideas for your trip. 
              Try asking for activities that match your interests, travel style, or specific preferences.
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Activity Detail Dialog */}
      {selectedActivity && (
        <ActivityDetailDialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          activity={selectedActivity}
          onSave={handleToggleSave}
          trip={trip}
        />
      )}
    </Box>
  );
};

export default ActivityGenerate;